import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { SystemSetting } from "../../../types/system-setting.type";
import { formatThaiShortDateTimeBkk } from "../../../utils/date";
import { AFFECTS_REPORTS_WARNING, SOURCE_LABEL, settingLabel } from "./settingMeta";
import { defaultValueHint, formatSettingValue } from "./settingValue";

const NEVER_EDITED_TEXT = "ยังไม่เคยแก้ไข";
const TOP_ALIGNED_CELL_SX = { verticalAlign: "top" } as const;
const KEY_SX = { fontFamily: "monospace" } as const;
const WARNING_ICON_SX = { fontSize: 16 } as const;

interface SettingRowProps {
  setting: SystemSetting;
  /** Why the value is not in use now, when another setting makes it moot. */
  note: string | null;
  onEdit: (setting: SystemSetting) => void;
  onOpenHistory: (setting: SystemSetting) => void;
}

export default function SettingRow({
  setting,
  note,
  onEdit,
  onOpenHistory,
}: Readonly<SettingRowProps>) {
  const handleEdit = () => onEdit(setting);
  const handleOpenHistory = () => onOpenHistory(setting);

  const defaultHint = defaultValueHint(setting);
  const updatedAt = formatThaiShortDateTimeBkk(setting.updated_at, { weekday: false });

  return (
    <TableRow>
      <TableCell sx={TOP_ALIGNED_CELL_SX}>
        <Typography variant="body2" fontWeight={600}>
          {settingLabel(setting.key)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={KEY_SX}>
          {setting.key}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {setting.description}
        </Typography>
      </TableCell>

      <TableCell sx={TOP_ALIGNED_CELL_SX}>
        <Stack spacing={0.75} alignItems="flex-start">
          <Typography variant="body1" fontWeight={700}>
            {formatSettingValue(setting.value, setting.key)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={SOURCE_LABEL[setting.source]} />
            {defaultHint && (
              <Typography variant="caption" color="text.secondary">
                {defaultHint}
              </Typography>
            )}
          </Stack>
          {note && (
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <InfoOutlinedIcon color="info" sx={WARNING_ICON_SX} />
              <Typography variant="caption" color="info.dark">
                {note}
              </Typography>
            </Stack>
          )}
          {setting.affects_reports && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <WarningAmberIcon color="warning" sx={WARNING_ICON_SX} />
              <Typography variant="caption" color="warning.dark">
                {AFFECTS_REPORTS_WARNING}
              </Typography>
            </Stack>
          )}
        </Stack>
      </TableCell>

      <TableCell sx={TOP_ALIGNED_CELL_SX}>
        {setting.updated_by ? (
          <>
            <Typography variant="body2">{setting.updated_by}</Typography>
            <Typography variant="caption" color="text.secondary">
              {updatedAt}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {NEVER_EDITED_TEXT}
          </Typography>
        )}
      </TableCell>

      <TableCell align="center" sx={TOP_ALIGNED_CELL_SX}>
        <Stack direction="row" justifyContent="center">
          <Tooltip title="แก้ไข">
            <IconButton size="small" color="primary" onClick={handleEdit}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="ประวัติการแก้ไข">
            <IconButton size="small" onClick={handleOpenHistory}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
